/**
 * Normaliza qualquer valor lançado/recebido em um `Error` de verdade antes de
 * `Sentry.captureException`.
 *
 * ─── DE ONDE VEIO ───────────────────────────────────────────────────────────
 *
 * PLAYPROSPORT-16 (31/08/2026, /experimentais em prod) chegou ao Sentry com o
 * título `Fi` — o nome minificado da função que chamou o capture — e a
 * mensagem genérica:
 *
 *   Error: Object captured as exception with keys: code, details, hint, message
 *
 * O erro real (`TypeError: Failed to fetch` contra o Supabase) só aparecia se
 * alguém abrisse o evento e lesse `extra.__serialized__`. Sem `type` e `value`
 * úteis, o Sentry agrupa por onde o capture foi chamado, não por qual erro
 * aconteceu: duas falhas diferentes no mesmo hook viram uma issue só, e a
 * mesma falha em dois hooks vira duas.
 *
 * A causa é a documentada em `@pps/mutations/useDomainMutation`: no padrão
 * usado em toda a base (`const { error } = await supabase.rpc(...)`), `error`
 * é o objeto plano `JSON.parse(body)` do postgrest-js — NUNCA uma instância
 * de `Error`. `captureException` recebe um objeto e sintetiza aquele título.
 *
 * ─── O QUE ESTA FUNÇÃO FAZ ──────────────────────────────────────────────────
 *
 * Devolve um `Error` cujo `name`/`message` são o erro que de fato aconteceu —
 * é isso que vira o título e a chave de agrupamento da issue. Quando o
 * `details` do Supabase carrega um stack de verdade — é o caso das falhas de
 * rede — ele vira o `stack` do Error, então os frames apontam para onde o fetch
 * quebrou e não para o `captureException`.
 *
 * `code`/`details`/`hint` ficam como propriedades do Error, e o objeto original
 * em `cause`, para quem inspecionar o erro EM CÓDIGO. Nenhum dos quatro chega
 * ao Sentry hoje: `captureException` serializa `name`,
 * `message` e `stack`; o `linkedErrorsIntegration` (ligado por default) só
 * segue um `cause` que seja instância de `Error`, e o nosso é objeto plano; e o
 * `extraErrorDataIntegration`, que publicaria as propriedades extras em
 * `contexts`, NÃO está na lista de `initSentry` (`./sentry.ts`). Ligá-lo é uma
 * linha — e é decisão em aberto, não esquecimento: `details` de erro do
 * Postgres carrega dado da linha que violou a constraint (`Key (email)=
 * (a@b.com) already exists.`) e o scrubber de PII (`./scrub.ts`) filtra por
 * NOME de chave, não inspeciona texto livre.
 *
 * Não filtra, não traduz e não decide se o erro merece ir ao Sentry: quem
 * chama já decidiu isso. Tradução de recusa de permissão para o usuário
 * continua em `@pps/mutations/rls-message` — aqui é o que o dev lê.
 */

/**
 * `Error` com `cause`. A propriedade — e a sobrecarga de dois argumentos do
 * construtor — só existe na lib `ES2022`, e este package é consumido como
 * FONTE (`main`/`types` apontam para `src/`), então quem compila cada arquivo
 * é a tsconfig do app: `apps/crm` e `apps/public` estão em `lib: ES2020`, onde
 * `new Error(msg, { cause })` não compila. Declarar aqui mantém o `cause`
 * tipado sem depender da lib de quem importa — no runtime dos 4 apps (Vite,
 * browsers de 2021 pra cá) a propriedade existe.
 *
 * Quem guarda essa restrição é `tsconfig.consumidores.json`, que compila este
 * `src/` na lib mais baixa entre os consumidores. Sem ele, `new Error(msg,
 * { cause })` passa no `typecheck` do package (ES2022) e só quebra no `tsc` do
 * CRM — que é exatamente o que aconteceu na primeira versão deste arquivo.
 */
interface ErroComCause extends Error {
  cause?: unknown;
}

/** `new Error(message, { cause })` escrito de forma compatível com ES2020. */
function erroComCause(message: string, cause: unknown): ErroComCause {
  const erro: ErroComCause = new Error(message);
  erro.cause = cause;
  return erro;
}

/** Erro do Supabase/postgrest promovido a `Error` com as chaves preservadas. */
export class SupabaseError extends Error {
  readonly code?: unknown;
  readonly details?: unknown;
  readonly hint?: unknown;

  constructor(
    message: string,
    fields: { code?: unknown; details?: unknown; hint?: unknown },
    cause: unknown,
  ) {
    super(message);
    this.name = "SupabaseError";
    this.code = fields.code;
    this.details = fields.details;
    this.hint = fields.hint;
    // Via `ErroComCause` e não `super(message, { cause })`: ver o comentário
    // daquela interface — `apps/crm` compila este arquivo em lib ES2020.
    const comCause: ErroComCause = this;
    comCause.cause = cause;
  }
}

/** Uma linha de stack trace do V8/SpiderMonkey (`    at foo (file:1:2)`). */
const STACK_FRAME = /^\s+at\s/;

function temMessageString(value: object): value is { message: string } {
  return (
    "message" in value && typeof (value as { message: unknown }).message === "string"
  );
}

/** Forma de `PostgrestError`/`FetchError`: message + code + details + hint. */
function ehErroSupabase(value: object): boolean {
  return (
    temMessageString(value) &&
    "code" in value &&
    "details" in value &&
    "hint" in value
  );
}

/**
 * Reaproveita o stack que o supabase-js empacotou em `details` (falhas de rede
 * trazem o stack do fetch em texto). Erros de Postgres colocam OUTRA coisa ali
 * — dado da linha que violou a constraint, inclusive — e esses ficam de fora.
 */
function stackDeDetails(details: unknown, header: string): string | null {
  if (typeof details !== "string") return null;
  const frames = details.split("\n").filter((linha) => STACK_FRAME.test(linha));
  if (frames.length === 0) return null;
  return [header, ...frames].join("\n");
}

function descreveValor(value: unknown): string {
  try {
    const json = JSON.stringify(value);
    if (json && json !== "{}") return `Non-Error thrown: ${json.slice(0, 200)}`;
  } catch {
    // Referência circular / BigInt — cai no String() abaixo.
  }
  return `Non-Error thrown: ${String(value)}`;
}

/**
 * Converte `error` em um `Error` pronto para `Sentry.captureException`.
 * `Error` recebido é devolvido intacto (não reembrulha, não perde stack).
 */
export function toSentryError(error: unknown): Error {
  if (error instanceof Error) return error;

  if (typeof error === "string") return new Error(error);

  if (error !== null && typeof error === "object") {
    if (ehErroSupabase(error)) {
      const { message, code, details, hint } = error as {
        message: string;
        code?: unknown;
        details?: unknown;
        hint?: unknown;
      };
      const promovido = new SupabaseError(message, { code, details, hint }, error);
      const stack = stackDeDetails(details, `SupabaseError: ${message}`);
      if (stack) promovido.stack = stack;
      return promovido;
    }

    if (temMessageString(error)) {
      return erroComCause(error.message, error);
    }

    return erroComCause(descreveValor(error), error);
  }

  if (error === null || error === undefined) return new Error("Unknown error");

  return erroComCause(descreveValor(error), error);
}
